import React, { useState, useEffect, useRef, useCallback } from 'react';
import { posteingangApi, vertraegeApi } from '../api';

// ─── Status-Konfiguration ─────────────────────────────────────────────────────

const STATUS_CONFIG = {
  NEU: { label: 'Neu', color: '#94a3b8', bg: '#1e293b', icon: '🕐' },
  WIRD_VERARBEITET: { label: 'Verarbeitung…', color: '#818cf8', bg: '#1e1b4b', icon: '⚙️' },
  ERKANNT: { label: 'Erkannt', color: '#a78bfa', bg: '#1e1b4b', icon: '🔍' },
  ZU_PRUEFEN: { label: 'Prüfen', color: '#fbbf24', bg: '#422006', icon: '⚠️' },
  ZUGEORDNET: { label: 'Zugeordnet', color: '#34d399', bg: '#052e16', icon: '✅' },
  VERARBEITET: { label: 'Verarbeitet', color: '#34d399', bg: '#052e16', icon: '✔' },
  FEHLER: { label: 'Fehler', color: '#f87171', bg: '#431407', icon: '❌' },
  DUPLIKAT: { label: 'Duplikat', color: '#fdba74', bg: '#422006', icon: '🔁' },
  IGNORIERT: { label: 'Ignoriert', color: '#64748b', bg: '#1e293b', icon: '🚫' },
};

const CONTENT_SOURCE_LABEL = {
  PDF_TEXT: { label: 'PDF-Text', color: '#34d399' },
  OCR: { label: 'OCR erkannt', color: '#818cf8' },
  FILENAME_FALLBACK: { label: 'Dateiname (Fallback)', color: '#fbbf24' },
  NONE: { label: 'Kein Text', color: '#64748b' },
};

const FILTER_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'ZU_PRUEFEN', label: 'Zu prüfen' },
  { value: 'ERKANNT', label: 'Erkannt' },
  { value: 'NEU', label: 'Neu' },
  { value: 'ZUGEORDNET', label: 'Zugeordnet' },
  { value: 'DUPLIKAT', label: 'Duplikat' },
  { value: 'IGNORIERT', label: 'Ignoriert' },
  { value: 'FEHLER', label: 'Fehler' },
];

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function formatBytes(b) {
  if (!b) return '–';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
  return (b / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(s) {
  if (!s) return '–';
  return new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

function konfidenzLabel(k) {
  if (k === 'HIGH') return { label: 'Hoch', color: '#059669' };
  if (k === 'MEDIUM') return { label: 'Mittel', color: '#D97706' };
  if (k === 'LOW') return { label: 'Niedrig', color: '#DC2626' };
  return { label: '–', color: '#6B7280' };
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const page = { padding: '24px', maxWidth: 1100, margin: '0 auto' };
const summaryBar = {
  display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20
};
const summaryChip = (active) => ({
  padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: '2px solid transparent',
  transition: 'all .15s',
  ...(active ? { borderColor: '#818cf8', background: '#1e1b4b', color: '#818cf8' } : { background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' })
});
const uploadZone = (dragging) => ({
  border: `2px dashed ${dragging ? '#818cf8' : '#334155'}`,
  borderRadius: 12, padding: '28px 20px', textAlign: 'center',
  background: dragging ? '#1e1b4b' : '#1e293b',
  cursor: 'pointer', marginBottom: 20, transition: 'all .15s'
});
const card = {
  border: '1px solid #334155', borderRadius: 10, padding: '14px 18px',
  marginBottom: 10, cursor: 'pointer', background: '#1e293b',
  transition: 'box-shadow .15s', display: 'flex', alignItems: 'flex-start', gap: 14
};
const cardHover = { boxShadow: '0 2px 8px rgba(0,0,0,.4)' };
const badge = (s) => {
  const cfg = STATUS_CONFIG[s] || STATUS_CONFIG.NEU;
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
    color: cfg.color, background: cfg.bg
  };
};
const drawer = {
  position: 'fixed', top: 0, right: 0, width: 420, maxWidth: '100%', height: '100vh',
  background: '#1e293b', boxShadow: '-4px 0 24px rgba(0,0,0,.5)',
  zIndex: 1000, overflowY: 'auto', padding: 24, borderLeft: '1px solid #334155'
};
const btnPrimary = {
  padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
  background: '#818cf8', color: '#fff', fontWeight: 600, fontSize: 14
};
const btnSecondary = {
  padding: '8px 18px', borderRadius: 8, border: '1px solid #334155',
  cursor: 'pointer', background: '#334155', color: '#94a3b8', fontWeight: 600, fontSize: 14
};
const btnDanger = {
  padding: '8px 18px', borderRadius: 8, border: 'none',
  cursor: 'pointer', background: '#ef4444', color: '#fff', fontWeight: 600, fontSize: 14
};

// ─── Komponenten ──────────────────────────────────────────────────────────────

function SummaryBar({ summary, activeFilter, onFilter }) {
  const total = summary?.gesamt || 0;
  const pruefen = summary?.ZU_PRUEFEN || 0;
  const fehler = summary?.FEHLER || 0;

  return (
    <div style={summaryBar}>
      <button style={summaryChip(!activeFilter)} onClick={() => onFilter('')}>
        📥 Alle ({total})
      </button>
      <button style={summaryChip(activeFilter === 'ZU_PRUEFEN')} onClick={() => onFilter('ZU_PRUEFEN')}>
        ⚠️ Zu prüfen ({pruefen})
      </button>
      {fehler > 0 && (
        <button style={summaryChip(activeFilter === 'FEHLER')} onClick={() => onFilter('FEHLER')}>
          ❌ Fehler ({fehler})
        </button>
      )}
      <button style={summaryChip(activeFilter === 'ZUGEORDNET')} onClick={() => onFilter('ZUGEORDNET')}>
        ✅ Zugeordnet ({summary?.ZUGEORDNET || 0})
      </button>
      {(summary?.DUPLIKAT || 0) > 0 && (
        <button style={summaryChip(activeFilter === 'DUPLIKAT')} onClick={() => onFilter('DUPLIKAT')}>
          🔁 Duplikate ({summary.DUPLIKAT})
        </button>
      )}
      {(summary?.IGNORIERT || 0) > 0 && (
        <button style={summaryChip(activeFilter === 'IGNORIERT')} onClick={() => onFilter('IGNORIERT')}>
          🚫 Ignoriert ({summary.IGNORIERT})
        </button>
      )}
    </div>
  );
}

function UploadZone({ onUpload, uploading }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onUpload(files[0]);
  };

  const handleFile = (e) => {
    if (e.target.files[0]) onUpload(e.target.files[0]);
    e.target.value = '';
  };

  return (
    <div
      style={uploadZone(dragging)}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={handleFile} />
      {uploading ? (
        <span style={{ color: '#2563EB', fontWeight: 600 }}>⚙️ Wird hochgeladen…</span>
      ) : (
        <>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Dokument hier ablegen oder klicken</div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>PDF, JPG, PNG, WEBP – max. 20 MB</div>
        </>
      )}
    </div>
  );
}

function InboxCard({ item, onClick }) {
  const [hover, setHover] = useState(false);
  const cfg = STATUS_CONFIG[item.intake_status] || STATUS_CONFIG.NEU;
  const kl = konfidenzLabel(item.bester_match_konfidenz);

  return (
    <div
      style={{ ...card, ...(hover ? cardHover : {}) }}
      onClick={() => onClick(item)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ fontSize: 28, flexShrink: 0 }}>{cfg.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.originaldateiname || item.dateiname || '–'}
          </span>
          <span style={badge(item.intake_status)}>{cfg.label}</span>
        </div>
        <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span>{formatDate(item.erstellt_am)}</span>
          <span>{formatBytes(item.groesse)}</span>
          {item.klassifikation && <span>📄 {item.klassifikation}</span>}
          {item.bester_match_vertrag_titel && (
            <span style={{ color: kl.color, fontWeight: 500 }}>
              → {item.bester_match_vertrag_titel} ({item.bester_match_score}%, {kl.label})
            </span>
          )}
          {item.zugeordnet_vertrag_titel && (
            <span style={{ color: '#059669', fontWeight: 600 }}>✅ {item.zugeordnet_vertrag_titel}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailDrawer({ item, vertraege, onClose, onAssign, onRetry, onIgnorieren, loading }) {
  const [selectedVertrag, setSelectedVertrag] = useState('');
  const [confirming, setConfirming] = useState(false);

  if (!item) return null;
  const cfg = STATUS_CONFIG[item.intake_status] || STATUS_CONFIG.NEU;
  const kl = konfidenzLabel(item.bester_match_konfidenz);

  const handleAssign = async () => {
    if (!selectedVertrag) return;
    setConfirming(true);
    try { await onAssign(item.id, parseInt(selectedVertrag)); } finally { setConfirming(false); }
  };

  return (
    <div style={drawer}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Eingang-Details</h2>
        <button onClick={onClose} style={{ ...btnSecondary, padding: '4px 12px' }}>✕</button>
      </div>

      {/* Status & Datei */}
      <div style={{ marginBottom: 16 }}>
        <span style={badge(item.intake_status)}>{cfg.icon} {cfg.label}</span>
      </div>
      <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 4, wordBreak: 'break-all' }}>
          {item.originaldateiname || item.dateiname}
        </div>
        <div style={{ fontSize: 13, color: '#6B7280', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          <span>Größe: {formatBytes(item.groesse)}</span>
          <span>Typ: {item.dateityp || '–'}</span>
          <span>Hochgeladen: {formatDate(item.erstellt_am)}</span>
          <span>Verarbeitet: {formatDate(item.verarbeitet_am)}</span>
        </div>
        {item.content_source && item.content_source !== 'NONE' && (() => {
          const src = CONTENT_SOURCE_LABEL[item.content_source] || { label: item.content_source, color: '#6B7280' };
          return (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              Textquelle: <span style={{ color: src.color, fontWeight: 600 }}>{src.label}</span>
              {item.content_length > 0 && <span style={{ color: '#9CA3AF' }}> ({item.content_length} Zeichen)</span>}
            </div>
          );
        })()}
      </div>

      {/* Klassifikation */}
      {item.klassifikation && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Erkannter Dokumenttyp</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...badge('ERKANNT'), fontSize: 13 }}>📄 {item.klassifikation}</span>
            {item.klassifikation_konfidenz != null && (
              <span style={{ fontSize: 12, color: '#6B7280' }}>
                ({Math.round(item.klassifikation_konfidenz * 100)}% Konfidenz)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Bester Match */}
      {item.bester_match_vertrag_titel && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Beste Vertragszuordnung</div>
          <div style={{
            background: item.bester_match_konfidenz === 'HIGH' ? '#ECFDF5' : '#FFFBEB',
            borderRadius: 8, padding: '10px 14px', fontSize: 14
          }}>
            <div style={{ fontWeight: 600 }}>{item.bester_match_vertrag_titel}</div>
            <div style={{ color: kl.color, fontSize: 13 }}>
              Score: {item.bester_match_score}% – Konfidenz: {kl.label}
            </div>
            {item.bester_match_begruendung && (
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{item.bester_match_begruendung}</div>
            )}
          </div>
        </div>
      )}

      {/* Vorschläge */}
      {item.zuordnungs_vorschlaege?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Alle Vorschläge</div>
          {item.zuordnungs_vorschlaege.map((v, i) => (
            <div key={i} style={{
              fontSize: 13, padding: '6px 10px', marginBottom: 4,
              background: '#F9FAFB', borderRadius: 6, display: 'flex', justifyContent: 'space-between'
            }}>
              <span>{v.titel || v.anbieter || `Vertrag ${v.id || v.vertrag_id}`}</span>
              <span style={{ color: '#6B7280' }}>{Math.round((v.konfidenz || 0) * 100)}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Extrahierte Felder */}
      {item.extrahierte_felder?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Erkannte Felder</div>
          <div style={{ maxHeight: 180, overflowY: 'auto', background: '#F9FAFB', borderRadius: 8, padding: '8px 12px' }}>
            {item.extrahierte_felder.map((f, i) => (
              <div key={i} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #E5E7EB' }}>
                <span style={{ color: '#374151', fontWeight: 500 }}>{f.feldname}</span>
                <span style={{ color: '#6B7280' }}>{f.normalisierter_wert ?? f.erkannter_wert ?? '–'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zugeordnet */}
      {item.intake_status === 'ZUGEORDNET' && item.zugeordnet_vertrag_titel && (
        <div style={{ background: '#ECFDF5', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: '#059669' }}>✅ Zugeordnet</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>{item.zugeordnet_vertrag_titel}</div>
        </div>
      )}

      {/* Duplikat-Info */}
      {item.intake_status === 'DUPLIKAT' && (
        <div style={{ background: '#FEF3C7', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: '#92400E' }}>🔁 Duplikat erkannt</div>
          <div style={{ fontSize: 13, marginTop: 4, color: '#78350F' }}>
            Diese Datei wurde bereits hochgeladen.
            {item.duplikat_referenz && (
              <span> Original: <strong>{item.duplikat_referenz.originaldateiname || `Eingang #${item.duplikat_referenz.id}`}</strong>
                {' '}({formatDate(item.duplikat_referenz.erstellt_am)})
              </span>
            )}
            {item.duplikat_eingang_id && !item.duplikat_referenz && (
              <span> Original: Eingang #{item.duplikat_eingang_id}</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#92400E', marginTop: 6 }}>
            Nutzen Sie "Ignorieren" um den Eingang zu schließen, oder "Erneut verarbeiten" um ihn neu zu starten.
          </div>
        </div>
      )}

      {/* Ignoriert-Info */}
      {item.intake_status === 'IGNORIERT' && (
        <div style={{ background: '#F3F4F6', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: '#6B7280' }}>🚫 Ignoriert</div>
          <div style={{ fontSize: 13, marginTop: 4, color: '#6B7280' }}>
            Dieser Eingang wurde nicht-destruktiv geschlossen. Sie können ihn jederzeit mit "Erneut verarbeiten" wieder aktivieren.
          </div>
        </div>
      )}

      {/* Fehler */}
      {item.fehler && (
        <div style={{ background: '#FEF2F2', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: '#DC2626' }}>Fehler</div>
          <div style={{ fontSize: 13, marginTop: 4, color: '#7F1D1D' }}>{item.fehler}</div>
        </div>
      )}

      {/* Manuelle Zuweisung für ZU_PRUEFEN / ERKANNT */}
      {(item.intake_status === 'ZU_PRUEFEN' || item.intake_status === 'ERKANNT') && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Vertrag manuell zuordnen</div>
          <select
            value={selectedVertrag}
            onChange={(e) => setSelectedVertrag(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #D1D5DB', marginBottom: 10 }}
          >
            <option value="">-- Vertrag auswählen --</option>
            {vertraege.map(v => (
              <option key={v.id} value={v.id}>{v.titel}{v.anbieter ? ` (${v.anbieter})` : ''}</option>
            ))}
          </select>
          <button
            style={{ ...btnPrimary, width: '100%', opacity: (!selectedVertrag || confirming) ? .6 : 1 }}
            disabled={!selectedVertrag || confirming}
            onClick={handleAssign}
          >
            {confirming ? 'Wird zugeordnet…' : '✅ Zuordnung bestätigen'}
          </button>
        </div>
      )}

      {/* Change Detection */}
      {item.change_detection && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: '#f1f5f9' }}>Änderungsvergleich</div>
          <div style={{
            background: item.change_detection.summary?.overallDirection === 'WORSENING' ? '#431407'
              : item.change_detection.summary?.overallDirection === 'IMPROVEMENT' ? '#052e16' : '#1e293b',
            border: `1px solid ${item.change_detection.summary?.overallDirection === 'WORSENING' ? '#ef444433' : item.change_detection.summary?.overallDirection === 'IMPROVEMENT' ? '#22c55e33' : '#334155'}`,
            borderRadius: 8, padding: '10px 14px', fontSize: 13
          }}>
            <div style={{ fontWeight: 600, color: item.change_detection.summary?.overallDirection === 'WORSENING' ? '#f87171' : item.change_detection.summary?.overallDirection === 'IMPROVEMENT' ? '#34d399' : '#94a3b8' }}>
              {item.change_detection.summary?.overallDirection === 'WORSENING' ? '⬇ Verschlechterung'
                : item.change_detection.summary?.overallDirection === 'IMPROVEMENT' ? '⬆ Verbesserung'
                : '↔ Keine wesentliche Änderung'}
            </div>
            {item.change_detection.differences?.length > 0 && (
              <div style={{ marginTop: 6, color: '#94a3b8' }}>
                {item.change_detection.differences.length} Änderung{item.change_detection.differences.length !== 1 ? 'en' : ''} erkannt
              </div>
            )}
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>INDIKATOR – heuristischer Vergleich</div>
          </div>
        </div>
      )}

      {/* Ignorieren-Button (für offene und Duplikat-Eingänge) */}
      {['ERKANNT', 'ZU_PRUEFEN', 'DUPLIKAT', 'FEHLER', 'NEU'].includes(item.intake_status) && (
        <button
          style={{ ...btnSecondary, width: '100%', marginBottom: 8, color: '#6B7280' }}
          onClick={() => onIgnorieren(item.id)}
        >
          🚫 Ignorieren
        </button>
      )}

      {/* Retry */}
      {['FEHLER', 'ERKANNT', 'DUPLIKAT', 'IGNORIERT'].includes(item.intake_status) && (
        <button
          style={{ ...btnSecondary, width: '100%', marginBottom: 10 }}
          onClick={() => onRetry(item.id)}
        >
          ↻ Erneut verarbeiten
        </button>
      )}

      {/* Verarbeitungslog */}
      {item.verarbeitungs_log && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: '#6B7280' }}>Verarbeitungslog anzeigen</summary>
          <pre style={{ fontSize: 11, background: '#F9FAFB', padding: 10, borderRadius: 8, overflowX: 'auto', marginTop: 8, whiteSpace: 'pre-wrap' }}>
            {item.verarbeitungs_log}
          </pre>
        </details>
      )}
    </div>
  );
}

// ─── Hauptseite ───────────────────────────────────────────────────────────────

export default function PosteingangPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [vertraege, setVertraege] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const pollRef = useRef(null);

  const showToast = (msg, color = '#059669') => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = useCallback(async (filter = statusFilter) => {
    try {
      const data = await posteingangApi.list(filter ? { status: filter } : {});
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch (e) {
      setError(e.message);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
    vertraegeApi.list().then(r => setVertraege(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
    // Poll alle 5s solange WIRD_VERARBEITET-Items vorhanden
    const poll = () => {
      pollRef.current = setTimeout(async () => {
        await loadData();
        poll();
      }, 5000);
    };
    poll();
    return () => clearTimeout(pollRef.current);
  }, []);

  useEffect(() => { loadData(statusFilter); }, [statusFilter]);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await posteingangApi.upload(fd);
      showToast('✅ Dokument hochgeladen – Verarbeitung läuft');
      await loadData();
    } catch (e) {
      showToast('❌ Upload fehlgeschlagen: ' + e.message, '#DC2626');
    } finally {
      setUploading(false);
    }
  };

  const handleSelectItem = async (item) => {
    setSelected(item.id);
    setLoading(true);
    try {
      const detail = await posteingangApi.get(item.id);
      setSelectedDetail(detail);
    } catch (e) {
      showToast('Fehler beim Laden der Details', '#DC2626');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (intakeId, vertragId) => {
    try {
      await posteingangApi.zuordnen(intakeId, vertragId);
      showToast('✅ Dokument erfolgreich zugeordnet');
      setSelectedDetail(null);
      setSelected(null);
      await loadData();
    } catch (e) {
      showToast('Fehler: ' + e.message, '#DC2626');
    }
  };

  const handleRetry = async (intakeId) => {
    try {
      await posteingangApi.retry(intakeId);
      showToast('↻ Verarbeitung neu gestartet');
      setSelectedDetail(null);
      setSelected(null);
      await loadData();
    } catch (e) {
      showToast('Fehler: ' + e.message, '#DC2626');
    }
  };

  const handleIgnorieren = async (intakeId) => {
    try {
      await posteingangApi.ignorieren(intakeId);
      showToast('🚫 Eingang ignoriert');
      setSelectedDetail(null);
      setSelected(null);
      await loadData();
    } catch (e) {
      showToast('Fehler: ' + e.message, '#DC2626');
    }
  };

  return (
    <div style={page}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.color, color: '#fff', padding: '12px 20px',
          borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,.2)'
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>📥 Posteingang</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>
          Dokumente automatisch erkennen und Verträgen zuordnen
        </p>
      </div>

      {/* Upload */}
      <UploadZone onUpload={handleUpload} uploading={uploading} />

      {/* Statusfilter */}
      {summary && (
        <SummaryBar summary={summary} activeFilter={statusFilter} onFilter={setStatusFilter} />
      )}

      {/* Fehler */}
      {error && (
        <div style={{ background: '#FEF2F2', borderRadius: 8, padding: 12, color: '#DC2626', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Liste */}
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {statusFilter ? `Keine Einträge mit Status "${STATUS_CONFIG[statusFilter]?.label}"` : 'Posteingang leer'}
          </div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            Laden Sie ein Dokument hoch – es wird automatisch analysiert und einem Vertrag zugeordnet.
          </div>
        </div>
      ) : (
        items.map(item => (
          <InboxCard
            key={item.id}
            item={item}
            onClick={handleSelectItem}
          />
        ))
      )}

      {/* Detail-Drawer */}
      {selectedDetail && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', zIndex: 999 }}
            onClick={() => { setSelectedDetail(null); setSelected(null); }}
          />
          <DetailDrawer
            item={selectedDetail}
            vertraege={vertraege}
            onClose={() => { setSelectedDetail(null); setSelected(null); }}
            onAssign={handleAssign}
            onRetry={handleRetry}
            onIgnorieren={handleIgnorieren}
            loading={loading}
          />
        </>
      )}
    </div>
  );
}
